import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Axios from "../../../axios/axios";
import { Header } from "../../../components/SpearParts/Layouts/Header";
import { SearchSection } from "../../../components/Others/Layout/SearchSection";
import { Footer } from "../../../components/SpearParts/Layouts/Footer";
import { toTitleCase } from "../../../components/utils/StringUtil";
import NoData from "../../../components/NoData/Index";
import BuildingAdvancedSearch from "./BuildingAdvancedSearch";
import { searchPart } from "../Home/OtherHome";
import BldMiddle from "../../../components/Others/Layout/BldMiddle";
import TopNavBar from "../../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../../components/SpearParts/Layouts/TopSearchBar";
import { resolveEntityName, getProductName, getProductPartNo, getProductYear } from "../../../components/utils/EntityResolver";
import './Building.css'


export default function OtherProductList() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const filters = {
    brandId: searchParams.get("brandId"),
    manufacturerId: searchParams.get("manufacturerId"),
    categoryId: searchParams.get("categoryId"),
    subcategoryId: searchParams.get("subcategoryId"),
  };

  const [bldMaterials, setBldMaterials] = useState([]);
  const [otherCategory, setOtherCategory] = useState([]);
  const [otherBrand, setOtherBrand] = useState([]);
  const [otherModels, setOtherModels] = useState([]);
  const [region, setRegion] = useState([]);
  const [originalSearchResults, setOriginalSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [qualityFilter, setQualityFilter] = useState("");
  const itemsPerPage = 10;

  const dedupeProducts = (arr) => {
    const seen = new Set();
    return arr.filter((item) => {
      const uniqueKey =
        (item?.meterialName || "") +
        "|" +
        (item?.brandId?._id || item?.brandId || "") +
        "|" +
        (item?.categoryId?._id || item?.categoryId || "") +
        "|" +
        (item?.year || "");

      if (seen.has(uniqueKey)) return false;
      seen.add(uniqueKey);
      return true;
    });
  };

    const fetchMaterials = async () => {
    try {
      const response = await searchPart(
        { ...filters, ...advancedFilters, partQuality: qualityFilter },
        currentPage,
        itemsPerPage
      );

      setBldMaterials(Array.isArray(response.data) ? response.data : []);
      setOriginalSearchResults(
        Array.isArray(response.data) ? response.data : []
      );
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      setBldMaterials([]);
      setOriginalSearchResults([]);
      setTotalPages(1);
      setTotalCount(0);
    }
  };

  const fetchData = async () => {
    try {
      const { data: categories } = await Axios.get("/categories");
      const { data: brands } = await Axios.get("/brand");
      const { data: models } = await Axios.get("/model");
      const { data: regions } = await Axios.get("/regions");

      setOtherCategory(categories);
      setOtherBrand(brands);
      setOtherModels(models);
      setRegion(regions);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [currentPage, location.search, advancedFilters, qualityFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  const navigate = useNavigate();

  const clearAllFilters = () => {
    setAdvancedFilters({});
    setQualityFilter("");
    navigate(location.pathname, { replace: true });
  };

  const removeSingleFilter = (keyToRemove) => {
    if (keyToRemove === "partQuality") {
      setQualityFilter("");
      return;
    }
    const updatedAdv = { ...advancedFilters };
    delete updatedAdv[keyToRemove];
    setAdvancedFilters(updatedAdv);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(keyToRemove);
    const newSearch = searchParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  const getFilterLabel = (key, val) => {
    if (!val) return null;
    if (key === "categoryId") return `Category: ${getCategoryName(val, null)}`;
    if (key === "subcategoryId") return `Subcategory: ${val}`;
    if (key === "brandId") return `Brand: ${getBrandName(val)}`;
    if (key === "manufacturerId") return `Manufacturer: ${val}`;
    if (key === "modelId") return `Model: ${getModelName(val)}`;
    if (key === "regionId") return `Region: ${getRegionName(val)}`;
    if (key === "year") return `Year: ${val}`;
    if (key === "features") return `Feature: ${val}`;
    if (key === "application") return `Application: ${val}`;
    if (key === "partQuality") return `Quality: ${toTitleCase(val)}`;
    return `${toTitleCase(key)}: ${val}`;
  };

  const urlSearchParamsObj = Object.fromEntries(new URLSearchParams(location.search).entries());
  const combinedActiveFilters = { ...urlSearchParamsObj, ...advancedFilters };
  if (qualityFilter) combinedActiveFilters["partQuality"] = qualityFilter;
  delete combinedActiveFilters["page"];
  delete combinedActiveFilters["limit"];

  const activeFilterEntries = Object.entries(combinedActiveFilters)
    .filter(([k, v]) => v !== undefined && v !== null && v !== "")
    .map(([key, val]) => ({ key, label: getFilterLabel(key, val) }))
    .filter(item => item.label);

  const hasActiveFilters = activeFilterEntries.length > 0;

  const getBrandName = (brandId) => resolveEntityName(brandId, otherBrand, "Unknown Brand");
  const getModelName = (modelId) => resolveEntityName(modelId, otherModels, "Unknown Model");
  const getCategoryName = (categoryId, material) => resolveEntityName(categoryId || material?.subcategoryId || material?.category, otherCategory, "Unknown Category");
  const getRegionName = (regionId) => resolveEntityName(regionId, region, "Unknown Region");

  const handleQualityFilter = (quality) => {

    setCurrentPage(1);
    if (quality === "all") {
      setBldMaterials(originalSearchResults);
    } else {
      const filtered = originalSearchResults.filter(
        (p) => p.partQuality?.toLowerCase() === quality
      );
      setBldMaterials(filtered);
    }
  };

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: "url(/images/bnr-bg.png)",
  };

  const beforeImageStyle = {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    width: "360px",
    height: "100%",
    backgroundImage: "url(/images/others.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left top",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  const afterImageStyle = {
    content: '""',
    position: "absolute",
    right: 0,
    bottom: "900px",
    width: "200px",
    height: "90%",
    backgroundImage: "url(/images/other-after.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right bottom",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  return (
    <>
      <div className="New-Rnt-Dashboard" style={sectionStyle}>
        {/* <Header /> */}
        <div className="before-image" style={beforeImageStyle}></div>
        {/* <div className="after-image" style={afterImageStyle}></div> */}
        <div className="Main-div">
          <TopNavBar />
          <div className="TopSearchBar">
            <TopSearchBar />
          </div>
          <div className="container">
            <div className="bnr-div">
              <BldMiddle initialFilters={filters} />
            </div>

            <div
              className="BuildingProductList"
              // style={{
              //   width: "90%",
              //   alignContent: "center",
              //   margin: "auto",
              //   display: "flex",
              // }}
            >
              {/* LEFT SIDEBAR – ADVANCED FILTER */}
              <div className="col-lg-3">
                <div className="advanced-filter-card p-3">
                  <BuildingAdvancedSearch
                    categoryId={filters.categoryId}
                    onResults={(f) => {
                      setAdvancedFilters(f);
                      setCurrentPage(1); // reset pagination
                    }}
                  />
                </div>
              </div>

              {/* RIGHT SIDE – RESULTS */}
              <div className="col-lg-9 Bld-results"  >
                {hasActiveFilters && (
                  <div className="active-filters-container mb-3 p-3 bg-light border rounded d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="fw-bold text-dark me-2">Active Filters:</span>
                      {activeFilterEntries.map(({ key, label }) => (
                        <span
                          key={key}
                          className="badge bg-primary text-white d-inline-flex align-items-center gap-2 px-3 py-2"
                          style={{ fontSize: "13px", fontWeight: "500", borderRadius: "20px" }}
                        >
                          {label}
                          <span
                            style={{ cursor: "pointer", fontWeight: "bold", marginLeft: "4px" }}
                            onClick={() => removeSingleFilter(key)}
                            title="Remove filter"
                          >
                            ✕
                          </span>
                        </span>
                      ))}
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger px-3 py-1 fw-bold"
                      onClick={clearAllFilters}
                      style={{ borderRadius: "20px" }}
                    >
                      ✕ Clear All Filters
                    </button>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="m-0">
                    Search results ({bldMaterials.length})
                  </h6>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() => {
                        setQualityFilter("");
                        setCurrentPage(1);
                      }}
                    >
                      All
                    </button>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => {
                        setQualityFilter("Genuine");
                        setCurrentPage(1);
                      }}
                    >
                      Genuine
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setQualityFilter("Aftermarket");
                        setCurrentPage(1);
                      }}
                    >
                      Aftermarket
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table search-table">
                    <tbody>
                      {/* PRODUCT HEADER ROW */}
                      <tr>
                        <td colSpan="10" className="p-0">
                          <div className="product-header-row">
                            <div className="head-cell name">Name</div>
                            <div className="head-cell">Part No</div>
                            <div className="head-cell">Brand</div>
                            {/* <div className="head-cell">Model</div>             */}
                            <div className="head-cell">Category</div>
                            <div className="head-cell">Year</div>
                            <div className="head-cell icon"></div>
                          </div>
                        </td>
                      </tr>

                      {/* PRODUCT DATA ROWS */}
                      <tr>
                        <td colSpan="8" className="border-td p-0">
                          <table className="table search-inner-table">
                            <tbody>
                              {dedupeProducts(bldMaterials).length > 0 ? (
                                dedupeProducts(bldMaterials).map(
                                  (material, index) => (
                                    <tr
                                      key={index}
                                      className="product-row-card"
                                    >
                                      <td colSpan="8">
                                        <div className="product-row">
                                          {/* NAME */}
                                          <div className="cell name">
                                            <Link
                                              to={`/others/product/single/${material._id}`}
                                              state={{
                                                bldMaterials,
                                                otherCategory,
                                                otherBrand,
                                                otherModels,
                                                region,
                                              }}
                                            >
                                              {toTitleCase(
                                                getProductName(material)
                                              )}
                                            </Link>
                                          </div>

                                          {/* PART NUMBER */}
                                          <div className="cell">
                                            {getProductPartNo(material)}
                                          </div>

                                          {/* BRAND */}
                                          <div className="cell">
                                            {toTitleCase(
                                              getBrandName(material.brandId)
                                            )}
                                          </div>

                                          {/* MODEL */}
                                          {/* <div className="cell">
    {toTitleCase(
      getModelName(material.modelId)
    )}
  </div> */}

                                          {/* CATEGORY */}
                                          <div className="cell">
                                            {toTitleCase(
                                              getCategoryName(material.categoryId, material)
                                            )}
                                          </div>

                                          {/* YEAR */}
                                          <div className="cell">
                                            {getProductYear(material)}
                                          </div>


                                          {/* GO ICON */}
                                          <div className="cell icon">
                                            <Link
                                              to={`/others/product/single/${material._id}`}
                                              state={{
                                                bldMaterials,
                                                otherCategory,
                                                otherBrand,
                                                otherModels,
                                                region,
                                              }}
                                            >
                                              <img
                                                src="/images/go.png"
                                                alt="Go"
                                              />
                                            </Link>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )
                                )
                              ) : (
                                <tr>
                                  <td colSpan="8" className="text-center">
                                    <NoData />
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* PAGINATION */}
                  <div className="pagination-container d-flex justify-content-end align-items-center mt-4">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                    >
                      <i className="fa fa-arrow-left"></i>
                    </button>

                    <span className="mx-3">
                      {currentPage} of {totalPages}
                    </span>

                    <button
                      className="pagination-btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                    >
                      <i className="fa fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <Footer /> */}
      </div>
    </>
  );
}
